import React from "react";
import { Table, Typography, Avatar, Tag, Empty } from "antd";
import {
  FilePdfOutlined,
  FileImageOutlined,
  FileOutlined,
  LockOutlined,
  UnlockOutlined,
  UserOutlined,
} from "@ant-design/icons";
import moment from "moment";

const { Link } = Typography;

const getFileIcon = (filename) => {
  if (!filename) return <FileOutlined style={{ color: "#1890ff", fontSize: 18 }} />;
  const extension = filename.split(".").pop().toLowerCase();
  switch (extension) {
    case "pdf":
      return <FilePdfOutlined style={{ color: "red", fontSize: 18 }} />;
    case "png":
    case "jpg":
    case "jpeg":
      return <FileImageOutlined style={{ color: "#52c41a", fontSize: 18 }} />;
    default:
      return <FileOutlined style={{ color: "#1890ff", fontSize: 18 }} />;
  }
};

const VisibilityTag = ({ isSharedWithPatient }) => {
  return (
    <Tag
      icon={isSharedWithPatient ? <UnlockOutlined /> : <LockOutlined />}
      color={isSharedWithPatient ? "blue" : "default"}
      style={{ textTransform: "capitalize" }}
    >
      {isSharedWithPatient ? "Shared" : "Private"}
    </Tag>
  );
};

/**
 * @param {Array} documents - [{ fileRef, title, filename, dateAdded, isSharedWithPatient, providerName, providerProfilePhoto, providerInitials }]
 * @param {Function} onDownload - (fileRef, filename) => void
 * @param {boolean} showVisibility - render the VISIBILITY column
 * @param {boolean} showProvider - render the PROVIDER column
 * @param {boolean} loading
 * @param {string} emptyText
 */
const DocumentTable = ({
  documents = [],
  onDownload,
  showVisibility = false,
  showProvider = false,
  loading = false,
  emptyText = "No documents yet",
}) => {
  const columns = [
    {
      title: "TITLE",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "FILE NAME",
      dataIndex: "filename",
      key: "filename",
      render: (filename, record) => (
        <Link
          onClick={() => onDownload && onDownload(record.fileRef, filename)}
          style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
        >
          {getFileIcon(filename)}
          {filename}
        </Link>
      ),
    },
    showVisibility && {
      title: "VISIBILITY",
      dataIndex: "isSharedWithPatient",
      key: "isSharedWithPatient",
      render: (isSharedWithPatient) => <VisibilityTag isSharedWithPatient={isSharedWithPatient} />,
    },
    showProvider && {
      title: "PROVIDER",
      dataIndex: "providerName",
      key: "providerName",
      render: (providerName, record) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <Avatar
            size={24}
            src={record.providerProfilePhoto || undefined}
            {...(record.providerInitials
              ? { children: record.providerInitials }
              : { icon: <UserOutlined /> })}
          />
          {providerName || "—"}
        </span>
      ),
    },
    {
      title: "DATE ADDED",
      dataIndex: "dateAdded",
      key: "dateAdded",
      render: (dateAdded) => (
        <span style={{ color: "#8c8c8c" }}>
          {dateAdded ? moment(dateAdded).format("MMM D, YYYY") : "—"}
        </span>
      ),
    },
  ].filter(Boolean);

  return (
    <Table
      columns={columns}
      dataSource={documents}
      rowKey={(record) => record.fileRef || record.id}
      pagination={false}
      loading={loading}
      locale={{
        emptyText: <Empty description={emptyText} image={Empty.PRESENTED_IMAGE_SIMPLE} />,
      }}
    />
  );
};

export default DocumentTable;
